import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {FlowState} from '@gongxigames/framework/slot/modules/flow/states/FlowState';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {FlowModule} from '@gongxigames/framework/slot/modules/flow/FlowModule';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';

export class FlowBasicState extends FlowState {
    public onEnterState(): void {
        if (this.isFeatureFinished()) {
            this.emit(FlowModuleEvents.FEATURE_FINISHED);
            this.setupPaidRoundEndEvent();
            this.emitRoundEnded();
        }

        this.emit(FlowModuleEvents.ENTER_BASIC_STATE);
    }

    public onLeaveState(): void {
        super.onLeaveState();

        if (this.isFeatureStarted()) {
            this.emit(FlowModuleEvents.FEATURE_STARTED);
        }
    }

    protected isFeatureStarted(): boolean {
        return this.module.currentState.name === FlowModule.BASIC_STATE && this.module.logic.nextAction !== Action.SPIN;
    }

    protected isFeatureFinished(): boolean {
        return this.module.currentState.name === FlowModule.BASIC_STATE && this.module.logic.previousAction !== Action.SPIN;
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.setEventHandler(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped.bind(this));
        this.setEventHandler(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed.bind(this));
        this.setEventHandler(PickBonusEvents.STARTED, this.onPickBonusStarted.bind(this));
        this.setEventHandler(FlowModuleEvents.RESPIN_WIN, this.onRespinWon.bind(this));
        this.setEventHandler(WinPresentationEvents.PAY_ANIMATION_STARTED, this.onPayAnimationStarted.bind(this));
        this.setEventHandler(BuyBonusPopUpEvents.BUY_BUTTON_CLICKED, this.onBuyButtonClicked.bind(this));
    }

    protected startCoinFlightFeature(): void {
        if (this.module.logic.isCoinFlightFeature(this.module.getRubyCoinSymbolId!)) {
            this.emit(FlowModuleEvents.START_COIN_FLIGHT_FEATURE);
        }
    }

    protected onAllReelsStopped(): void {
        this.startCoinFlightFeature();
        this.clearEventsQueue();
        this.addCallbackToWaitingLIst(FlowModuleEvents.CAN_SPIN, this.emitRoundEnded.bind(this));
        this.addCallbackToWaitingLIst(FlowModuleEvents.FREESPINS_WIN, this.emitRoundEnded.bind(this));
        this.addCallbackToWaitingLIst(FlowModuleEvents.RESPIN_WIN, this.emitRoundEnded.bind(this));
        this.addCallbackToWaitingLIst(FlowModuleEvents.SELECT_BONUS_START, this.emitRoundEnded.bind(this));
        this.addCallbackToWaitingLIst(FlowModuleEvents.BONUS_GAME_WON, this.emitRoundEnded.bind(this));

        switch (this.module.logic.nextAction) {
            case Action.SPIN: {
                this.setupLinesWinMainGameSequence();
                break;
            }
            case Action.SELECT: {
                this.setupSelectBonusSequence();
                break;
            }
            case Action.FREESPIN: {
                // we have won free spins
                if (this.module.logic.previousAction !== Action.FREESPIN) {
                    this.setupWinBeforeFreeGames();
                }
                break;
            }

            case Action.PICK: {
                this.emit(FlowModuleEvents.BONUS_GAME_WON);
                this.setupPickBonusSequence();
                break;
            }

            case Action.RESPIN: {
                this.setupRespin();
                break;
            }

            default:
                break;
        }
    }

    protected setupRespin(): void {
        if (this.module.resourceManager.isResourcesLoaded) {
            this.emit(FlowModuleEvents.HAS_RESPIN_ON_NEXT_SPIN);

            if (this.isWin()) {
                if (this.isBigWin()) {
                    this.setupBigWinBeforeRespin();
                } else {
                    this.setupWinBeforeRespin();
                }
            } else {
                this.setupNoWinBeforeRespin();
            }
        } else {
            this.addToWaitingForResources(this.setupRespin);
        }
    }

    protected setupLinesWinMainGameSequence(): void {
        if (this.isWin()) {
            if (this.isBigWin()) {
                this.setupBigWin();
            } else {
                this.setupWin();
            }
        } else {
            this.setupNoWin();
        }
    }

    protected setupNoWin(): void {
        this.emit(FlowModuleEvents.NO_WIN);
        this.emit(FlowModuleEvents.CAN_SPIN);
        this.setupPaidRoundEndEvent();
    }

    protected setupBigWin(): void {
        if (this.module.resourceManager.isResourcesLoaded) {
            this.emit(FlowModuleEvents.WON);
            this.emit(FlowModuleEvents.WON_BIG_WIN);
            this.emit(ReelsEvents.DISABLE_SPIN_BY_SPIN_BUTTON);
            this.addEventToWaitingList(BigWinEvents.FINISHED, ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON);
            this.addEventToWaitingList(ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON, FlowModuleEvents.CAN_SPIN);
            this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.CAN_SPIN);
            this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.CAN_SPIN);

            this.setupPaidRoundEndEvent(BigWinEvents.FINISHED);
        } else {
            this.addToWaitingForResources(this.setupBigWin);
        }
    }

    protected setupBigWinBeforeRespin(): void {
        this.emit(FlowModuleEvents.WON);
        this.emit(FlowModuleEvents.WON_BIG_WIN);
        this.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.RESPIN_WIN);
    }

    protected setupWin(): void {
        this.setupPaidRoundEndEvent(['General.WinPresentation.WIN_METER_FINISHED', 'General.WinPresentation.WIN_METER_STOPPED', WinPresentationEvents.INITIAL_ANIMATION_SKIPPED]);
        this.emit(FlowModuleEvents.WON);
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.CAN_SPIN);
        this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.CAN_SPIN);
    }

    protected setupSelectBonusSequence(): void {
        if (this.module.resourceManager.isResourcesLoaded) {
            this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.SELECT_BONUS_START);
            this.emit(FlowModuleEvents.WON);
        } else {
            this.addToWaitingForResources(this.setupSelectBonusSequence);
        }
    }

    protected setupPickBonusSequence(): void {
        if (this.module.resourceManager.isResourcesLoaded) {
            if (this.isWin()) {
                this.setupWinBeforePickBonus();
            } else {
                this.emit(FlowModuleEvents.START_PICK_BONUS);
            }
        } else {
            this.addToWaitingForResources(this.setupPickBonusSequence);
        }
    }

    protected setupWinBeforePickBonus(): void {
        this.emit(FlowModuleEvents.WON);
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.START_PICK_BONUS);
    }

    protected setupNoWinBeforeRespin(): void {
        this.emit(FlowModuleEvents.RESPIN_WIN);
    }

    protected setupWinBeforeRespin(): void {
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.RESPIN_WIN);
        this.emit(FlowModuleEvents.WON);
    }

    protected setupWinBeforeFreeGames(): void {
        if (this.module.resourceManager.isResourcesLoaded) {
            const fsWon: number = this.module.logic.getFreeGamesCount();

            this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.FREESPINS_WIN, fsWon);
            this.emit(FlowModuleEvents.WON);
        } else {
            this.addToWaitingForResources(this.setupWinBeforeFreeGames);
        }
    }

    protected onPopupClosed(type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.gotoFreeSpins();
        }
    }

    protected gotoFreeSpins(): void {
        this.module.gotoState(FlowModule.FREE_SPINS_STATE);
        this.emit(FlowModuleEvents.CAN_SPIN);
    }

    protected onPickBonusStarted(): void {
        this.module.gotoState(FlowModule.PICK_STATE);
    }

    protected onRespinWon(): void {
        this.module.gotoState(FlowModule.RESPIN_BASIC_STATE);
    }

    protected onPayAnimationStarted(): void {
        this.module.eventManager.emit(FlowModuleEvents.CAN_SPIN);
    }

    protected onBuyButtonClicked(): void {
        this.module.gotoState(FlowModule.BUY_BONUS_STATE);
    }

    protected setupPaidRoundEndEvent(event?: symbol | string | (symbol | string)[]): void {
        const roundInfo = this.module.logic.getRoundInfo();

        if (event) {
            if (Array.isArray(event)) {
                event.forEach((e) => {
                    this.addEventToWaitingListWithPriority(e, 'General.Flow.PAID_ROUND_ENDED', 9999, roundInfo);
                });

                this.removeEventsFromEventsQueueAfterEvent('General.Flow.PAID_ROUND_ENDED', event);
            } else {
                this.addEventToWaitingListWithPriority(event, 'General.Flow.PAID_ROUND_ENDED', 9999, roundInfo);
            }
        } else {
            this.emit('General.Flow.PAID_ROUND_ENDED', roundInfo);
        }
    }
}
